/** @file Deterministic 64-bit content hash (two 32-bit lanes) of a JSON-like value with sorted keys. */

/** An object with its own JSON form (a Date gives its ISO time), read the way JSON.stringify reads it. */
const hasToJSON = (value: object): value is { toJSON: () => unknown } => typeof (value as { toJSON?: unknown }).toJSON === 'function';

/** `Tag[...]` with the items sorted, so insertion order never matters; no JSON text starts with a letter and `[`. */
const tagged = (tag: string, items: string[]): string => `${tag}[${items.sort().join(',')}]`;

/**
 * JSON text with object keys sorted and undefined fields dropped (a bare undefined reads as null). Maps hash their
 * entries and Sets their values by content in any order, tagged so neither equals an array or object; toJSON objects
 * (Date) hash their JSON form (review M3).
 */
function stable(value: unknown): string {
  if (value === undefined) return 'null';
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  if (typeof value !== 'object' || value === null) return JSON.stringify(value);
  if (value instanceof Map) return tagged('Map', [...(value as Map<unknown, unknown>)].map(([k, v]) => `${stable(k)}:${stable(v)}`));
  if (value instanceof Set) return tagged('Set', [...(value as Set<unknown>)].map(stable));
  if (hasToJSON(value)) return stable(value.toJSON());
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : 1));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stable(v)}`).join(',')}}`;
}

const hex8 = (n: number): string => (n >>> 0).toString(16).padStart(8, '0');

/**
 * 16 hex chars; FNV-1a in one lane, a multiply-xorshift in the other (cache keys, never security). Hashes plain JSON,
 * Maps, Sets and toJSON objects by content; any other object by its own enumerable keys.
 */
export function contentHash(value: unknown): string {
  const text = stable(value);
  let h1 = 0x811c9dc5;
  let h2 = 0x9e3779b9;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193);
    h2 = Math.imul(h2 ^ c, 0x5bd1e995);
    h2 ^= h2 >>> 15;
  }
  return hex8(h1) + hex8(h2);
}
